import React from "react";
import { Award, CalendarCheck, CalendarX } from "lucide-react";
import { StreakBadge } from "./StreakBadge";
import { monthLabel } from "../lib/dates";

function DuesPill({ balance }) {
  if (!balance?.paid_through) return null;
  const ahead = balance.months_ahead || 0;
  const arrears = balance.months_in_arrears || 0;
  const good = arrears === 0;
  const text = ahead > 0 ? `Paid ${ahead} mo ahead · through ${monthLabel(balance.paid_through)}`
    : good ? `Dues paid through ${monthLabel(balance.paid_through)}`
      : `${arrears} mo behind · paid through ${monthLabel(balance.paid_through)}`;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${good ? "bg-palm/20 text-emerald-200" : "bg-crimson/30 text-rose-100"}`} data-testid="dues-paid-through-pill">
      {good ? <CalendarCheck size={12} /> : <CalendarX size={12} />} {text}
    </span>
  );
}

export function StatusPill({ status }) {
  const map = {
    "GOOD STANDING": "bg-green-100 text-green-800 border-green-300",
    "DUES PENDING": "bg-amber-100 text-amber-800 border-amber-300",
    "NOT IN GOOD STANDING": "bg-red-100 text-red-800 border-red-300",
  };
  return (
    <span data-testid="good-standing-status-badge"
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-mono font-semibold tracking-wide ${map[status] || map["DUES PENDING"]}`}>
      {status}
    </span>
  );
}

function membershipYears(joinDate) {
  if (!joinDate) return { years: 0, anniversaryMonth: false };
  const join = new Date(joinDate), now = new Date();
  const beforeAnniversary = now.getMonth() < join.getMonth() || (now.getMonth() === join.getMonth() && now.getDate() < join.getDate());
  const years = now.getFullYear() - join.getFullYear() - (beforeAnniversary ? 1 : 0);
  return { years, anniversaryMonth: join.getMonth() === now.getMonth() && years >= 1 };
}

function AnniversaryBadge({ years, anniversaryMonth }) {
  if (years < 1) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${anniversaryMonth ? "border-gold bg-gold text-crimson animate-pulse" : "border-gold/50 text-gold"}`} data-testid="anniversary-badge">
      <Award size={12} /> {years} {years === 1 ? "year" : "years"}{anniversaryMonth ? " · Anniversary!" : ""}
    </span>
  );
}

function Stat({ label, value }) {
  return <div><p className="text-parchment/60">{label}</p><p className="font-mono-accent text-lg font-semibold">{value}</p></div>;
}

const BG = { good: "linear-gradient(135deg,#1E4D2B 0%,#0f3018 100%)", bad: "linear-gradient(135deg,#7A0C0C 0%,#5C0909 100%)" };

export default function GoodStandingCard({ standing, member }) {
  if (!standing) return null;
  const { years, anniversaryMonth } = membershipYears(member?.join_date);
  return (
    <div data-testid="good-standing-card"
      className="relative overflow-hidden rounded-2xl border border-gold/40 p-6 text-parchment shadow-xl"
      style={{ background: standing.good_standing ? BG.good : BG.bad }}>
      <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-gold/10 blur-2xl" />
      <div className="flex items-start justify-between">
        <div>
          <p className="eyebrow text-gold">Digital Membership Card</p>
          <h3 className="font-serif-display text-2xl font-bold">{member?.name}</h3>
          <p className="font-mono-accent text-sm text-parchment/70">{member?.member_id}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusPill status={standing.status} />
          <DuesPill balance={standing.balance} />
          <AnniversaryBadge years={years} anniversaryMonth={anniversaryMonth} />
          <StreakBadge streak={standing.attendance.streak} compact />
        </div>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-4 text-sm">
        <Stat label="Balance" value={`$${standing.balance.balance.toFixed(2)}`} />
        <Stat label="Attendance" value={`${standing.attendance.percentage}%`} />
        <Stat label="Member Since" value={`${standing.membership_years}y`} />
      </div>
      {standing.outstanding_items?.length > 0 && (
        <ul className="mt-5 space-y-1 border-t border-white/15 pt-4 text-xs text-parchment/80">
          {standing.outstanding_items.map((it) => <li key={it}>• {it}</li>)}
        </ul>
      )}
      <p className="mt-4 text-[10px] uppercase tracking-widest text-parchment/50">
        Ofie! Biakoye! · Welfare {standing.welfare_eligible ? "eligible" : "locked"}
      </p>
    </div>
  );
}
